import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const INTEREST_OPTIONS = [
  'Piano Lessons',
  'Vocal Training & Coaching',
  'Drum Lessons',
  'Creative Coaching',
  'Neurodivergent Creative Development',
  'On-Site Program',
  'In-Home Program',
  'Recording Studio Services',
  'Live Entertainment / Event Services',
  'Photography, Videography & Media',
  'Audio, Visual & Technical Production',
  'Stage Production & Creative Support',
  'Creative Direction & Consulting',
  'General Inquiry',
];

const PLAN_OPTIONS = [
  { label: 'Foundation Plan', sub: '1 session/week · $160/mo', icon: 'ri-seedling-line', color: 'border-[#C7D2FE] bg-[#EEF2FF]', activeColor: 'border-[#4F46E5] bg-[#EEF2FF]', textColor: 'text-[#3730A3]' },
  { label: 'Standard Plan', sub: '2 sessions/week · $180/mo', icon: 'ri-fire-line', color: 'border-gray-200 bg-white', activeColor: 'border-[#1E3A8A] bg-[#1E3A8A]/5', textColor: 'text-[#1E3A8A]' },
  { label: 'Elite Development Plan', sub: '3 sessions/week · $200/mo', icon: 'ri-rocket-2-line', color: 'border-gray-200 bg-white', activeColor: 'border-brand-yellow bg-brand-yellow/5', textColor: 'text-brand-yellow-dark' },
];

const SESSION_TYPES = [
  {
    id: 'solo',
    label: 'Solo Session',
    sub: 'Just me — individual instruction',
    icon: 'ri-user-line',
    activeColor: 'border-[#1E3A8A] bg-[#EEF2FF]',
    color: 'border-gray-200 bg-white',
    textColor: 'text-[#1E3A8A]',
  },
  {
    id: 'group',
    label: 'Group Session',
    sub: 'Bringing a friend or more',
    icon: 'ri-group-line',
    activeColor: 'border-[#D97706] bg-[#FFFBEB]',
    color: 'border-gray-200 bg-white',
    textColor: 'text-[#D97706]',
  },
];

function EnrollForm({ initialPlan }: { initialPlan: string }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plan: initialPlan,
    interest: '',
    session_type: 'solo',
    friend_names: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) {
      setError('Message must be 500 characters or fewer.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => {
        if (key === 'friend_names' && formData.session_type === 'solo') return;
        body.append(key, val);
      });

      const res = await fetch('https://readdy.ai/api/form/d721ct4991ove783t8q0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to submit. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center py-10 text-center">
        {/* Success icon */}
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] mb-5">
          <i className="ri-check-double-line text-4xl text-brand-yellow" />
        </div>
        <span className="inline-block px-4 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow-dark font-body text-xs uppercase tracking-widest mb-4">
          Enrollment Received
        </span>
        <h3 className="font-heading text-3xl lg:text-4xl font-black text-brand-blue-dark mb-3">
          Thank You, {formData.name.split(' ')[0]}!
        </h3>
        <p className="font-body text-gray-500 text-base max-w-md leading-relaxed mb-8">
          We&apos;re so excited to welcome you to the Creative Institute family. Your enrollment request has been received and our team will be in touch within <strong className="text-brand-blue-dark">24 hours</strong> to get everything set up for you.
        </p>

        {/* Receipt card */}
        <div className="w-full max-w-md text-left rounded-3xl border-2 border-brand-yellow/30 overflow-hidden mb-8">
          {/* Receipt header */}
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] px-6 py-5 flex items-center justify-between">
            <div>
              <p className="font-body text-white/60 text-xs uppercase tracking-widest mb-0.5">Enrollment Summary</p>
              <p className="font-heading text-white text-lg font-black">Creative Institute LLC</p>
            </div>
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-yellow">
              <i className="ri-file-list-3-line text-brand-blue-dark text-lg" />
            </div>
          </div>

          {/* Receipt body */}
          <div className="bg-white px-6 py-5 flex flex-col gap-4">
            {[
              { label: 'Full Name', value: formData.name, icon: 'ri-user-line' },
              { label: 'Email', value: formData.email, icon: 'ri-mail-line' },
              ...(formData.phone ? [{ label: 'Phone', value: formData.phone, icon: 'ri-phone-line' }] : []),
              { label: 'Selected Plan', value: formData.plan || 'To be discussed', icon: 'ri-medal-line' },
              { label: 'Area of Interest', value: formData.interest || 'To be discussed', icon: 'ri-music-2-line' },
              { label: 'Session Type', value: formData.session_type === 'group' ? `Group Session${formData.friend_names ? ` — ${formData.friend_names}` : ''}` : 'Solo Session', icon: formData.session_type === 'group' ? 'ri-group-line' : 'ri-user-line' },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
                  <i className={`${row.icon} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-gray-400 text-xs uppercase tracking-wider">{row.label}</p>
                  <p className="font-body text-brand-blue-dark text-sm font-semibold truncate">{row.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Receipt footer */}
          <div className="bg-[#F8FAFF] px-6 py-4 border-t border-brand-yellow/20">
            <p className="font-body text-gray-400 text-xs text-center leading-relaxed">
              A copy of this request has been sent to <strong className="text-brand-blue-dark">{formData.email}</strong>
            </p>
          </div>
        </div>

        {/* What happens next */}
        <div className="w-full max-w-md text-left rounded-2xl bg-[#F0F4FF] border border-[#C7D2FE] p-6 mb-6">
          <p className="font-heading text-brand-blue-dark text-sm font-black uppercase tracking-widest mb-4">What Happens Next</p>
          <div className="flex flex-col gap-4">
            {[
              { num: '1', text: 'Our team reviews your enrollment request' },
              { num: '2', text: 'We call or email you within 24 hours to confirm details' },
              { num: '3', text: 'We schedule your first session and get you onboarded' },
              { num: '4', text: 'You show up, create, and thrive!' },
            ].map((step) => (
              <div key={step.num} className="flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-brand-yellow text-brand-blue-dark font-heading font-black text-xs flex-shrink-0">
                  {step.num}
                </div>
                <p className="font-body text-brand-blue-dark text-sm">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact line */}
        <p className="font-body text-gray-400 text-sm">
          Questions in the meantime?{' '}
          <a href="tel:9102424567" className="text-brand-purple font-semibold hover:underline cursor-pointer">
            Call us at 910-242-4567
          </a>
        </p>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="enroll-creative-institute"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* Plan Selector */}
      <div>
        <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-3 block">
          Selected Plan <span className="text-brand-yellow">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PLAN_OPTIONS.map((p) => {
            const isActive = formData.plan === p.label;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, plan: p.label }))}
                className={`cursor-pointer rounded-xl border-2 px-4 py-3.5 text-left transition-all duration-200 ${isActive ? p.activeColor : p.color}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${p.icon} text-base ${isActive ? p.textColor : 'text-gray-400'}`} />
                  <span className={`font-body text-xs font-bold ${isActive ? p.textColor : 'text-gray-500'}`}>{p.label}</span>
                </div>
                <p className={`font-body text-xs ${isActive ? p.textColor + '/80' : 'text-gray-400'}`}>{p.sub}</p>
                {isActive && (
                  <div className="mt-2 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line text-xs text-current" style={{ color: 'inherit' }} />
                    <span className={`font-body text-xs font-semibold ${p.textColor}`}>Selected</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <input type="hidden" name="plan" value={formData.plan} />
      </div>

      {/* Session Type */}
      <div>
        <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-3 block">
          Session Type <span className="text-brand-yellow">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {SESSION_TYPES.map((s) => {
            const isActive = formData.session_type === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, session_type: s.id }))}
                className={`cursor-pointer rounded-xl border-2 px-4 py-4 text-left transition-all duration-200 ${isActive ? s.activeColor : s.color}`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${isActive ? 'bg-white/60' : 'bg-gray-100'}`}>
                    <i className={`${s.icon} text-base ${isActive ? s.textColor : 'text-gray-400'}`} />
                  </div>
                  <span className={`font-body text-sm font-bold ${isActive ? s.textColor : 'text-gray-500'}`}>{s.label}</span>
                  {isActive && (
                    <span className="ml-auto flex-shrink-0">
                      <i className={`ri-checkbox-circle-fill text-base ${s.textColor}`} />
                    </span>
                  )}
                </div>
                <p className={`font-body text-xs pl-11 ${isActive ? s.textColor + '/80' : 'text-gray-400'}`}>{s.sub}</p>
              </button>
            );
          })}
        </div>
        <input type="hidden" name="session_type" value={formData.session_type} />

        {/* Friend name field — revealed when group is selected */}
        {formData.session_type === 'group' && (
          <div className="mt-4 p-4 rounded-xl bg-[#FFFBEB] border border-[#D97706]/25 animate-pulse-once">
            <label className="font-body text-xs font-semibold uppercase tracking-wider text-[#92400E] mb-2 block">
              <i className="ri-group-line mr-1" />
              Friend(s) Joining You <span className="text-gray-400 font-normal normal-case tracking-normal">— optional but helpful</span>
            </label>
            <input
              type="text"
              name="friend_names"
              value={formData.friend_names}
              onChange={handleChange}
              placeholder="e.g. Marcus Thompson, or &quot;2 friends&quot;"
              className="w-full px-4 py-3 rounded-xl border border-[#D97706]/30 font-body text-sm text-brand-blue-dark bg-white focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/10 transition-all duration-200 placeholder:text-gray-300"
            />
            <p className="font-body text-[#92400E]/70 text-xs mt-2 leading-snug">
              Let us know who's coming so we can prepare the right setup and ask about group rates for you.
            </p>
          </div>
        )}
      </div>

      {/* Name & Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-2 block">
            Full Name <span className="text-brand-yellow">*</span>
          </label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            className="w-full px-4 py-3.5 rounded-xl border border-gray-200 font-body text-sm text-brand-blue-dark bg-white focus:outline-none focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/10 transition-all duration-200 placeholder:text-gray-300"
          />
        </div>
        <div>
          <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-2 block">
            Email Address <span className="text-brand-yellow">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="w-full px-4 py-3.5 rounded-xl border border-gray-200 font-body text-sm text-brand-blue-dark bg-white focus:outline-none focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/10 transition-all duration-200 placeholder:text-gray-300"
          />
        </div>
      </div>

      {/* Phone & Interest */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-2 block">
            Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(910) 000-0000"
            className="w-full px-4 py-3.5 rounded-xl border border-gray-200 font-body text-sm text-brand-blue-dark bg-white focus:outline-none focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/10 transition-all duration-200 placeholder:text-gray-300"
          />
        </div>
        <div>
          <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-2 block">
            Area of Interest <span className="text-brand-yellow">*</span>
          </label>
          <select
            name="interest"
            required
            value={formData.interest}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-brand-purple transition-colors cursor-pointer"
          >
            <option value="">Select your interest...</option>
            {INTEREST_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="font-body text-xs font-semibold uppercase tracking-wider text-brand-blue-dark mb-2 block">
          Message <span className="text-gray-400 font-normal normal-case text-xs tracking-normal">(optional)</span>
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          placeholder="Tell us a bit about yourself or what you're looking for..."
          maxLength={500}
          className="w-full px-4 py-3.5 rounded-xl border border-gray-200 font-body text-sm text-brand-blue-dark bg-white focus:outline-none focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/10 transition-all duration-200 placeholder:text-gray-300 resize-none"
        />
        <div className={`flex justify-end mt-1 font-body text-xs ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
          {charCount}/500
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 font-body text-red-600 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || charCount > 500}
        className="whitespace-nowrap w-full py-5 rounded-xl bg-gradient-purple text-white font-body font-bold text-base hover:opacity-90 transition-all duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line text-lg animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-lg" />
            Submit Enrollment Request
          </>
        )}
      </button>
    </form>
  );
}

function FirstVisitSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const whatToBring = [
    { icon: 'ri-id-card-line', text: 'A valid ID or student ID' },
    { icon: 'ri-book-open-line', text: 'Any notes or goals you have for your creative journey' },
    { icon: 'ri-smartphone-line', text: 'Your phone or device if you want to take notes or photos' },
  ];

  const whatToExpect = [
    { icon: 'ri-car-line', title: 'We Come to You', desc: 'Our instructors travel directly to your home or preferred location — no commute, no hassle, just great instruction.' },
    { icon: 'ri-calendar-check-line', title: 'Flexible Scheduling', desc: 'Pick the days and times that work best for you. We work around your schedule, not the other way around.' },
    { icon: 'ri-time-line', title: 'Arrive a Few Minutes Early', desc: 'Give yourself 5–10 extra minutes on your first visit so we can check you in and get you comfortable before starting.' },
    { icon: 'ri-user-smile-line', title: 'Warm Welcome', desc: 'Our team will greet you, walk you through the space, answer any questions, and make sure you feel right at home.' },
    { icon: 'ri-discuss-line', title: 'Quick Intake Chat', desc: 'We\'ll have a short conversation about your goals and interests so we can tailor the experience to you from day one.' },
  ];

  return (
    <section ref={ref} className="py-24 bg-white border-t border-gray-100">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Header */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-16`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4">
            Your First Visit
          </span>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            Here&apos;s What to Expect<br />
            <span className="text-brand-purple">on Your First Session</span>
          </h2>
          <p className="font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            We want your first visit to Creative Institute to feel effortless. Here&apos;s everything you need to know before you walk through the door.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* What to Bring */}
          <div className={`scroll-hidden-left ${isVisible ? 'scroll-visible' : ''} flex-1 rounded-3xl bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] border border-brand-purple/10 p-8`}>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-brand-yellow flex-shrink-0">
                <i className="ri-luggage-cart-line text-lg" />
              </div>
              <h3 className="font-heading text-xl font-black text-brand-blue-dark">What to Bring</h3>
            </div>
            <div className="flex flex-col gap-4">
              {whatToBring.map((item) => (
                <div key={item.text} className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white text-brand-purple flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <p className="font-body text-brand-blue-dark text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 rounded-2xl bg-white border border-brand-purple/15">
              <p className="font-body text-brand-purple text-xs font-semibold uppercase tracking-widest mb-1">Good to Know</p>
              <p className="font-body text-gray-500 text-sm leading-relaxed">
                You don&apos;t need to bring any instruments or equipment — everything is provided by your instructor.
              </p>
            </div>
          </div>

          {/* What to Expect */}
          <div className={`scroll-hidden-right ${isVisible ? 'scroll-visible' : ''} flex-1 rounded-3xl bg-gradient-to-br from-[#FFFBF2] to-[#FEF3C7]/30 border border-brand-yellow/20 p-8`}>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-brand-yellow text-brand-blue-dark flex-shrink-0">
                <i className="ri-map-pin-2-line text-lg" />
              </div>
              <h3 className="font-heading text-xl font-black text-brand-blue-dark">When You Arrive</h3>
            </div>
            <div className="flex flex-col gap-5">
              {whatToExpect.map((item, idx) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    {idx < whatToExpect.length - 1 && (
                      <div className="w-px h-5 bg-brand-yellow/30 mt-1" />
                    )}
                  </div>
                  <div className="pb-1">
                    <p className="font-body text-brand-blue-dark text-sm font-semibold mb-0.5">{item.title}</p>
                    <p className="font-body text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom info bar */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 p-6 rounded-2xl bg-brand-blue-dark`}>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center text-brand-yellow">
                <i className="ri-map-pin-fill text-lg" />
              </div>
              <span className="font-body text-white text-sm font-semibold">Fayetteville, NC &amp; Surrounding Areas</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center text-brand-yellow">
                <i className="ri-time-fill text-lg" />
              </div>
              <span className="font-body text-white/80 text-sm">Mon – Sat: 9am – 9pm</span>
            </div>
          </div>
          <a
            href="tel:9102424567"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all flex-shrink-0"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm" />
            </div>
            Call for Pricing
          </a>
        </div>
      </div>
    </section>
  );
}

const enrollJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Enroll | Creative Institute LLC',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/enroll`,
  description: 'Enroll in Creative Institute LLC mobile music and arts programs. Choose from Foundation, Standard, or Elite Development plans. We come to you across North Carolina.',
  mainEntity: {
    '@type': 'Course',
    name: 'Creative Institute Mobile Music & Arts Programs',
    provider: {
      '@type': 'EducationalOrganization',
      name: 'Creative Institute LLC',
      telephone: '910-242-4567',
    },
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Foundation Plan', courseMode: 'onsite', description: '1 session per week — $160/mo' },
      { '@type': 'CourseInstance', name: 'Standard Plan', courseMode: 'onsite', description: '2 sessions per week — $180/mo' },
      { '@type': 'CourseInstance', name: 'Elite Development Plan', courseMode: 'onsite', description: '3 sessions per week — $200/mo' },
    ],
  },
};

export default function EnrollPage() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan') ?? '';
  const initialPlan = PLAN_OPTIONS.find(p => p.label === planParam)?.label ?? '';

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <main className="min-h-screen">
      <SEOHead
        title="Enroll Now | Creative Institute LLC — Mobile Music & Arts NC"
        description="Start your creative journey with Creative Institute LLC. Enroll in mobile music, vocal, drum, or arts programs across North Carolina. We come to you — flexible plans starting at $160/mo."
        keywords="enroll music lessons NC, Creative Institute enrollment, mobile arts program North Carolina, music lessons Fayetteville, in-home creative development"
        canonical="/enroll"
        jsonLd={enrollJsonLd}
      />
      {/* Hero */}
      <section className="relative py-32 overflow-hidden diagonal-cut">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/46771242b5077cf9e8cc1f8be528bcc9.jpg"
            alt="Creative Institute enrollment"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A2E]/90 via-[#0F172A]/80 to-[#1E1B4B]/70" />
        </div>
        <div ref={heroRef} className="relative z-10 max-w-screen-xl mx-auto px-8 text-center">
          <span className={`scroll-hidden ${heroVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest mb-6 bg-brand-yellow/10`}>
            Join the Creative Institute Family
          </span>
          <h1 className={`scroll-hidden delay-200 ${heroVisible ? 'scroll-visible' : ''} font-heading text-6xl lg:text-7xl font-black text-white mb-5`}>
            Begin Your<br />
            <span className="gradient-text">Creative Journey</span>
          </h1>
          <p className={`scroll-hidden delay-300 ${heroVisible ? 'scroll-visible' : ''} font-body text-white/70 text-lg max-w-2xl mx-auto leading-relaxed`}>
            Take the first step. Complete the form below and our team will connect with you to get started.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-[#F8F7FF]">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            {/* Left - Info */}
            <div className={`scroll-hidden-left ${formVisible ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[380px]`}>
              <h2 className="font-heading text-4xl font-black text-brand-blue-dark mb-5">
                Why Enroll?
              </h2>
              <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
                Creative Institute provides a structured, professional, and inclusive arts education environment where every student — of every age, every background, every learning style — is celebrated and supported.
              </p>

              {[
                { icon: 'ri-music-line', label: 'Multiple Arts Disciplines' },
                { icon: 'ri-heart-line', label: 'Inclusive & Neurodivergent-Friendly' },
                { icon: 'ri-group-line', label: 'Solo & Group Sessions Available' },
                { icon: 'ri-moon-line', label: 'Night Classes · Open Until 9pm' },
                { icon: 'ri-calendar-check-line', label: 'Flexible Scheduling' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4 py-3.5 border-b border-gray-100 last:border-0">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple flex-shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <span className="font-body text-brand-blue-dark text-sm font-medium">{item.label}</span>
                </div>
              ))}

              {/* Contact box */}
              <div className="mt-10 p-6 rounded-2xl bg-brand-blue-dark text-white">
                <h3 className="font-heading text-lg font-bold mb-4">Have Questions?</h3>
                <a
                  href="tel:9102424567"
                  className="flex items-center gap-3 mb-3 text-white/70 hover:text-white transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-brand-yellow text-base" />
                  </div>
                  <span className="font-body text-sm">910-242-4567</span>
                </a>
                <a
                  href="mailto:info@creativeinstitutellc.com"
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-brand-yellow text-base" />
                  </div>
                  <span className="font-body text-sm">info@creativeinstitutellc.com</span>
                </a>
              </div>
            </div>

            {/* Right - Form */}
            <div ref={formRef} className={`scroll-hidden-right ${formVisible ? 'scroll-visible' : ''} flex-1 bg-white rounded-3xl p-10 border border-brand-purple/10`}>
              <div className="mb-8">
                <h2 className="font-heading text-3xl font-black text-brand-blue-dark mb-2">
                  Enrollment Request
                </h2>
                <p className="font-body text-gray-400 text-sm">
                  Fill in the form below and we&apos;ll reach out within 24 hours.
                </p>
              </div>
              <EnrollForm initialPlan={initialPlan} />
            </div>
          </div>
        </div>
      </section>

      <FirstVisitSection />
    </main>
  );
}
